import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { filtersToListParams, type CatalogueFilters } from '@/lib/filters';
import type {
  AddLinkBody,
  AddLinkResult,
  CursorPage,
  LinkDetail,
  LinkSummary,
  UpdateLinkBody,
  UploadScreenshotBody,
} from '@/types/api';

/**
 * Cursor pagination, not offset: the API hands back an opaque `nextCursor` and we ask for the
 * next page with it. `getNextPageParam` returning undefined is what disables "Load more".
 */
export function useLinksQuery(
  filters: CatalogueFilters,
): UseInfiniteQueryResult<{ pages: CursorPage<LinkSummary>[] }, Error> {
  const params = filtersToListParams(filters);

  return useInfiniteQuery({
    queryKey: queryKeys.linkList(params),
    queryFn: ({ pageParam, signal }) =>
      apiClient.listLinks({ ...params, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: CursorPage<LinkSummary>) => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
  });
}

/**
 * `pollMs` re-reads the link on an interval — set while background work on it is in flight (see
 * useLinkProgress) so the page follows it to completion. Paused while the tab is hidden.
 */
export function useLinkQuery(
  linkId: string,
  options: { pollMs?: number | false } = {},
): UseQueryResult<LinkDetail, Error> {
  return useQuery<LinkDetail, Error>({
    queryKey: queryKeys.linkDetail(linkId),
    queryFn: ({ signal }) => apiClient.getLink(linkId, signal),
    enabled: linkId.length > 0,
    staleTime: 30_000,
    refetchInterval: options.pollMs ?? false,
  });
}

/**
 * Every link mutation writes the fresh detail into the cache and invalidates the (cursor
 * paginated, therefore un-patchable) list queries so the catalogue re-reads from the API.
 */
function useLinkMutationHandlers() {
  const queryClient = useQueryClient();
  return {
    onSuccess: (updated: LinkDetail) => {
      queryClient.setQueryData(queryKeys.linkDetail(updated.linkId), updated);
      void queryClient.invalidateQueries({ queryKey: queryKeys.links() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminFailures() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.facets() });
    },
  };
}

export function useUpdateLink() {
  const { onSuccess } = useLinkMutationHandlers();
  return useMutation<LinkDetail, Error, { linkId: string; body: UpdateLinkBody }>({
    mutationFn: ({ linkId, body }) => apiClient.updateLink(linkId, body),
    onSuccess,
  });
}

export function useRetryLink() {
  const { onSuccess } = useLinkMutationHandlers();
  return useMutation<LinkDetail, Error, string>({
    mutationFn: (linkId) => apiClient.retryLink(linkId),
    onSuccess,
  });
}

export function useRegenerateScreenshot() {
  const { onSuccess } = useLinkMutationHandlers();
  return useMutation<LinkDetail, Error, string>({
    mutationFn: (linkId) => apiClient.regenerateScreenshot(linkId),
    onSuccess,
  });
}

export function useUploadScreenshot() {
  const { onSuccess } = useLinkMutationHandlers();
  return useMutation<LinkDetail, Error, { linkId: string; body: UploadScreenshotBody }>({
    mutationFn: ({ linkId, body }) => apiClient.uploadScreenshot(linkId, body),
    onSuccess,
  });
}

/** Adding a link changes what the catalogue lists and what its filters count. */
export function useAddLink() {
  const queryClient = useQueryClient();
  return useMutation<AddLinkResult, Error, AddLinkBody>({
    mutationFn: (body) => apiClient.addLink(body),
    onSuccess: (result) => {
      if (result.outcome !== 'added') return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.links() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.facets() });
    },
  });
}

export function useDeleteLink() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (linkId) => apiClient.deleteLink(linkId),
    onSuccess: (_result, linkId) => {
      queryClient.removeQueries({ queryKey: queryKeys.linkDetail(linkId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.links() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminFailures() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.facets() });
    },
  });
}
